/**
 * Organization & Coach Analytics API
 *
 * RTK Query endpoints for organization and coach analytics dashboards.
 */

import { api } from '../api';

// ============================================
// Type Definitions
// ============================================

export interface OrgTrainingSummary {
  orgId: string;
  since?: string;
  totals: {
    modules: number;
    assignments: number;
    membersWithAssignments: number;
  };
  assignmentStatusCounts: {
    assigned: number;
    in_progress: number;
    completed: number;
    failed: number;
  };
  /** Null when there are no assignments (render "—", not 0%). */
  completionRate: number | null;
  averageScores: {
    overall?: number;
    clarity?: number;
    structure?: number;
    accuracy?: number;
    complexity?: number;
  };
  byModule: Array<{
    moduleId: string;
    moduleName: string;
    assignments: number;
    completionRate: number | null;
    avgOverallScore?: number;
  }>;
}

export interface OrgModuleDetail {
  moduleId: string;
  moduleName: string;
  domainKey: string;
  scenarioKey: string;
  npcPersonaLabel?: string;
  assignmentStatusCounts: {
    assigned: number;
    in_progress: number;
    completed: number;
    failed: number;
  };
  scoreDistribution: {
    overall: number[];
    clarity: number[];
    structure: number[];
    accuracy: number[];
    complexity: number[];
  };
  recentAssignments: Array<{
    assignmentId: string;
    assigneeUserId: string;
    assigneeName: string;
    status: string;
    lastScoreOverall?: number;
    lastCompletedAt?: string;
  }>;
}

export interface OrgPracticeSummary {
  orgId: string;
  since?: string;
  totalSessions: number;
  avgSessionLengthSeconds?: number;
  avgScores: {
    overall?: number;
    clarity?: number;
    structure?: number;
    accuracy?: number;
    complexity?: number;
  };
  byPersonaTier?: Array<{
    tier: string;
    sessions: number;
    avgOverallScore?: number;
  }>;
}

export interface OrgCoachingSummary {
  orgId: string;
  since?: string;
  activeEngagements: number;
  totalEngagements: number;
  coachingSessions: {
    requested: number;
    scheduled: number;
    completed: number;
    cancelled: number;
  };
  completedSessionCount: number;
  recentCompletedSessions: Array<{
    bookingId: string;
    coachUserId: string;
    coachName: string;
    clientUserId: string;
    clientName: string;
    completedAt: string;
    linkedPracticeSessionId?: string;
    lastScoreOverall?: number;
  }>;
}

export interface OrgUsageSummary {
  orgId: string;
  since?: string;
  practiceSessionsCount: number;
  coachingSessionsCount: number;
  contentQueriesCount: number;
  totalMembers: number;
  activeMembers: number;
}

export interface OrgHiringSummary {
  orgId: string;
  since?: string;
  totalPipelines: number;
  openPipelines: number;
  totalApplications: number;
  applicationsByStage: {
    applied: number;
    shortlisted: number;
    interviewing: number;
    offer: number;
    hired: number;
    rejected: number;
  };
  interviewStats: {
    totalInterviews: number;
    avgOverallScore?: number;
    avgTechnicalScore?: number;
    avgCulturalScore?: number;
  };
  hireConversionRate: number;
  byPipeline: Array<{
    pipelineId: string;
    pipelineTitle: string;
    applications: number;
    hired: number;
    rejected: number;
    inProgress: number;
  }>;
  recentHires: Array<{
    applicationId: string;
    candidateUserId: string;
    candidateName: string;
    pipelineTitle: string;
    hiredAt: string;
    verifiedAtApply: boolean;
  }>;
}

export interface OrgAnalyticsOverview {
  orgId: string;
  orgName?: string;
  since?: string;
  training: OrgTrainingSummary;
  practice: OrgPracticeSummary;
  coaching: OrgCoachingSummary;
  usage: OrgUsageSummary;
  hiring?: OrgHiringSummary;
}

export interface CoachAnalytics {
  coachUserId: string;
  engagements: {
    active: number;
    total: number;
  };
  sessions: {
    requested: number;
    scheduled: number;
    completed: number;
    cancelled: number;
  };
  avgScoresAcrossCoachedSessions?: {
    overall?: number;
    clarity?: number;
    structure?: number;
    accuracy?: number;
    complexity?: number;
  };
  byClient?: Array<{
    clientUserId: string;
    clientName: string;
    completedSessions: number;
    lastSessionAt?: string;
    avgOverallScore?: number;
  }>;
}

// ============================================
// API Definition
// ============================================

export const orgAnalyticsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // Organization Analytics

    getOrgAnalyticsOverview: builder.query<
      OrgAnalyticsOverview,
      { orgId: string; since?: string }
    >({
      query: ({ orgId, since }) => ({
        url: `/orgs/${orgId}/analytics/overview`,
        params: since ? { since } : undefined,
      }),
      providesTags: (_result, _error, { orgId }) => [
        { type: 'OrgAnalytics' as const, id: orgId },
      ],
    }),

    getOrgTrainingSummary: builder.query<
      OrgTrainingSummary,
      { orgId: string; since?: string }
    >({
      query: ({ orgId, since }) => ({
        url: `/orgs/${orgId}/analytics/training-summary`,
        params: since ? { since } : undefined,
      }),
      providesTags: (_result, _error, { orgId }) => [
        { type: 'OrgAnalytics' as const, id: `${orgId}-training` },
      ],
    }),

    getOrgModuleDetail: builder.query<
      OrgModuleDetail,
      { orgId: string; moduleId: string }
    >({
      query: ({ orgId, moduleId }) =>
        `/orgs/${orgId}/analytics/modules/${moduleId}/detail`,
      providesTags: (_result, _error, { orgId, moduleId }) => [
        { type: 'OrgAnalytics' as const, id: `${orgId}-module-${moduleId}` },
      ],
    }),

    getOrgCoachingSummary: builder.query<
      OrgCoachingSummary,
      { orgId: string; since?: string }
    >({
      query: ({ orgId, since }) => ({
        url: `/orgs/${orgId}/analytics/coaching-summary`,
        params: since ? { since } : undefined,
      }),
      providesTags: (_result, _error, { orgId }) => [
        { type: 'OrgAnalytics' as const, id: `${orgId}-coaching` },
      ],
    }),

    // Coach Analytics

    getMyCoachAnalytics: builder.query<CoachAnalytics, { since?: string } | void>({
      query: (args) => ({
        url: '/coach/analytics/me',
        params: args?.since ? { since: args.since } : undefined,
      }),
      providesTags: ['CoachAnalytics'],
    }),
  }),
});

export const {
  useGetOrgAnalyticsOverviewQuery,
  useGetOrgTrainingSummaryQuery,
  useGetOrgModuleDetailQuery,
  useGetOrgCoachingSummaryQuery,
  useGetMyCoachAnalyticsQuery,
} = orgAnalyticsApi;
